// Database restore client. Restore is now two-phase: prepare uploads the
// dump file and returns measurement + server-probe info; start finalises with
// the user-chosen options. The execution progress afterward reads through
// lib/progress.js::getProgress (the server runs it as a background job).
import { api } from './api.js';

// RESTORE_STAMP_SQL — the "Append restore timestamp" marker block, shared by the
// upload-restore dialog (RestoreDialog) and the from-server restore
// (ServerRestoreDialog) so the two never drift. Creates a small marker table in
// the restored database and stamps NOW() (server-side, version-safe to 5.0) —
// durable in-database proof of a clean restore, written only on success.
export const RESTORE_STAMP_SQL =
`CREATE TABLE IF NOT EXISTS \`ninocat_restore\` (
  \`id\` INT NOT NULL AUTO_INCREMENT PRIMARY KEY,
  \`restored_at\` DATETIME NOT NULL
);
INSERT INTO \`ninocat_restore\` (\`restored_at\`) VALUES (NOW());`;

// prepareRestoreUpload POSTs the dump as raw body so xhr.upload gives real
// byte-level upload progress. Resolves to {
//   prepareToken, total, maxStmtBytes,
//   serverMaxAllowedPacket, hasSuperPrivilege, targetMaxAllowedPacket
// } once the upload finishes and the server has counted/measured.
export function prepareRestoreUpload(connectionId, database, file, onUploadProgress) {
  return new Promise((resolve, reject) => {
    const url = `/api/connections/${connectionId}/databases/${encodeURIComponent(database)}/restore/prepare`;
    const xhr = new XMLHttpRequest();
    xhr.open('POST', url);
    xhr.withCredentials = true;
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && onUploadProgress) onUploadProgress(e.loaded, e.total);
    };
    xhr.onload = () => {
      let payload = null;
      try { payload = JSON.parse(xhr.responseText); } catch { /* non-JSON */ }
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve(payload || {});
      } else {
        reject(new Error(payload?.error || `upload failed (HTTP ${xhr.status})`));
      }
    };
    xhr.onerror = () => reject(new Error('network error during upload'));
    xhr.send(file);
  });
}

// startRestore finalises a prepared restore: server picks up the temp file +
// measurements via prepareToken, applies the user-chosen options, starts the
// background executor. Resolves to { token, total } for the progress poll.
export async function startRestore(connectionId, database, body) {
  return api.post(
    `/api/connections/${connectionId}/databases/${encodeURIComponent(database)}/restore`,
    body,
  );
}

// cancelRestorePrepare releases an unconsumed prep entry + its temp file when
// the user closes the dialog after upload but before clicking Restore. Best-
// effort — server treats a missing token as a no-op.
export async function cancelRestorePrepare(prepareToken) {
  try { await api.post(`/api/restore/prepare/${prepareToken}/cancel`, {}); }
  catch { /* benign */ }
}

// resolveRestore answers a paused restore: action ∈ "skip" | "skip-all" | "abort".
export async function resolveRestore(token, action) {
  return api.post(`/api/restore/${token}/resolve`, { action });
}

// prepareRestorePostSQL stashes optional "run after a successful restore" SQL
// and returns a one-shot token to pass to startRestore({ postSqlToken }).
export async function prepareRestorePostSQL(sql) {
  const r = await api.post('/api/restore-postsql', { sql });
  return r.token;
}
